import { useContext, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import type { LifeSpace } from '@/src/renderer/lib/db/models'
import { INTRO_SETTING, useIntroStatus, type IntroOffer, type IntroStatus, type PageHelpId } from '@/src/renderer/lib/intro'
import { onAppEvent } from '@/src/renderer/lib/events'
import { isAwaitingFirstSync, SyncContext } from '@/src/renderer/contexts/SyncProvider'
import { useIntroTextOptions } from '@/src/renderer/components/common/IntroTip'
import { writeSampleProject } from './sampleProject'
import { removeRetiredLearnTasks, writeLearnTasks } from './learnTasks'

/**
 * **Startet die Einführung** im Hauptfenster (#323), einmal nach frischen Folien (`intro.status = 'new'`) —
 * und noch einmal auf Hilfe › „Einführung neu starten“:
 * Beispielprojekt anlegen, Angebot der Lernaufgaben bereitlegen, „Entdecken“ einblenden.
 *
 * Erst nach dem ersten Sync und nur in einer leeren App: Ein zweites Gerät mit Cloud-Daten bekommt kein
 * zweites Beispielprojekt und kein Angebot (die Lernaufgaben gäbe es sonst doppelt) — „Entdecken“ schon.
 */
export function useIntroStart(activeLifeSpace: string): void {
  const lifeSpace: LifeSpace = activeLifeSpace === 'work' || activeLifeSpace === 'home' ? activeLifeSpace : 'personal'
  const source = useDataSource()
  const { t } = useTranslation()
  const { web, quickAddKeys } = useIntroTextOptions()
  const { status, loaded } = useIntroStatus()
  const empty = source.useAppIsEmpty()
  const awaitingSync = isAwaitingFirstSync(useContext(SyncContext))
  // Der Effekt hängt an Werten, die sich während des Anlegens ändern (`empty`) — einmal reicht.
  const started = useRef(false)

  useEffect(() => {
    if (started.current || !loaded || status !== 'new' || empty === undefined || awaitingSync) return
    started.current = true
    void (async () => {
      if (empty) await writeSampleProject(source, { t, lifeSpace, quickAddKeys })
      await source.setSetting<IntroOffer>(INTRO_SETTING.offer, empty ? 'pending' : 'none')
      await source.setSetting<IntroStatus>(INTRO_SETTING.status, 'running')
    })().catch(error => console.error('[intro] start failed', error))
  }, [loaded, status, empty, awaitingSync, source, t, lifeSpace, quickAddKeys])

  // Erst nach dem ersten Sync: Sonst lüde ein zweites Gerät die Lernaufgabe nach dem Löschen wieder herunter.
  const cleaned = useRef(false)
  useEffect(() => {
    if (cleaned.current || awaitingSync) return
    cleaned.current = true
    removeRetiredLearnTasks(source).catch(error => console.warn('[intro] cleanup failed', error))
  }, [awaitingSync, source])

  /*
    Hilfe › „Einführung neu starten“: Was fehlt, kommt wieder — Beispielprojekt, offene Lernaufgaben,
    „Entdecken“, getönte ?-Buttons. Die Haken in „Entdecken“ bleiben (wie im Prototyp): Getan ist getan.
  */
  useEffect(() => onAppEvent('intro-restart', () => {
    void (async () => {
      await source.setSetting<readonly PageHelpId[]>(INTRO_SETTING.helpSeen, [])
      await source.setSetting(INTRO_SETTING.discoverHidden, false)
      await writeSampleProject(source, { t, lifeSpace, quickAddKeys })
      await writeLearnTasks(source, { t, lifeSpace, web, quickAddKeys })
      await source.setSetting<IntroOffer>(INTRO_SETTING.offer, 'accepted')
      await source.setSetting<IntroStatus>(INTRO_SETTING.status, 'running')
    })().catch(error => console.error('[intro] restart failed', error))
  }), [source, t, lifeSpace, web, quickAddKeys])
}
